import { motion } from 'motion/react'
import type { WeakSpotItem } from '../../api/types'
import { Panel, TOKENS } from '../ui/dashboard-primitives'
import { Md } from './Md'

type Group = 'Needs work' | 'Working' | 'Open'
const GROUP_ORDER: Group[] = ['Needs work', 'Working', 'Open']

function groupOf(severity: string): Group {
  if (severity === 'risk' || severity === 'warning') return 'Needs work'
  if (severity === 'success') return 'Working'
  return 'Open'
}

function metricColor(severity: string): string {
  if (severity === 'risk' || severity === 'warning') return TOKENS.terracotta
  if (severity === 'success') return TOKENS.amber
  return TOKENS.creamDim
}

function chipStyle(severity: string) {
  if (severity === 'success')
    // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
    return { background: 'rgba(232,197,160,0.12)', borderColor: 'rgba(232,197,160,0.28)', color: TOKENS.amber }
  if (severity === 'risk' || severity === 'warning')
    // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
    return { background: TOKENS.terracottaSoft, borderColor: 'rgba(198,110,77,0.32)', color: TOKENS.terracotta }
  return { background: TOKENS.creamHairline, borderColor: TOKENS.creamBorder, color: TOKENS.creamDim }
}

/**
 * Six signals as equal peers (decision D6). Structure is grouping by what each
 * asks of you and a shared metric column — never a ranked lead signal.
 */
export function SignalsPanel({ signals }: { signals: WeakSpotItem[] }) {
  const grouped = GROUP_ORDER.map((g) => ({
    group: g,
    items: signals.filter((s) => groupOf(s.severity) === g),
  })).filter((g) => g.items.length > 0)

  return (
    <Panel variant="light" className="grid mt-2">
      {grouped.map(({ group, items }, gi) => (
        <div key={group}>
          <p
            className="text-[0.5625rem] tracking-[0.28em] uppercase pb-3"
            style={{
              fontFamily: 'var(--font-mono)',
              color: TOKENS.creamDim,
              marginTop: gi === 0 ? 0 : 30,
              paddingTop: gi === 0 ? 0 : 26,
              borderTop: gi === 0 ? undefined : '1px solid ' + TOKENS.creamBorder,
            }}
          >
            {group}
          </p>
          {items.map((s, i) => (
            <motion.article
              key={`${s.label}-${i}`}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              className="grid items-baseline gap-x-7 gap-y-1.5 py-4.25"
              style={{
                gridTemplateColumns: 'minmax(0,8.5rem) 1fr auto',
                borderTop: i === 0 ? undefined : '1px solid ' + TOKENS.creamHairline,
              }}
            >
              <span
                className="tabular-nums leading-[1.05]"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  fontSize: '1.5rem',
                  letterSpacing: '-0.028em',
                  color: metricColor(s.severity),
                }}
              >
                {s.metric}
              </span>
              <div className="min-w-0">
                <h3 className="text-base font-medium" style={{ color: TOKENS.cream, letterSpacing: '-0.01em' }}>
                  {s.label}
                </h3>
                <p className="mt-1.5 text-[0.875rem] leading-[1.6]" style={{ color: TOKENS.creamSoft, maxWidth: '82ch' }}>
                  <Md>{s.description}</Md>
                </p>
              </div>
              <span
                className="justify-self-end inline-flex items-center px-2.5 py-1 rounded-full text-[10px] tracking-[0.14em] uppercase border whitespace-nowrap"
                style={{ fontFamily: 'var(--font-mono)', ...chipStyle(s.severity) }}
              >
                {s.chip_label}
              </span>
            </motion.article>
          ))}
        </div>
      ))}
    </Panel>
  )
}
